(() => {
  const SENTINEL_CLASS = 'rr-infinite-sentinel';
  const ROOT_MARGIN_PX = 900;

  class CollectionInfiniteScroll {
    constructor() {
      this.loading = false;
      this.observer = null;
      this.nextUrl = null;
      this.bindFacets();
      this.bindScrollFallback();
      this.reset();
    }

    bindFacets() {
      const tryBind = () => {
        if (typeof FacetFiltersForm === 'undefined') return false;
        if (FacetFiltersForm.__rrInfiniteBound) return true;
        FacetFiltersForm.__rrInfiniteBound = true;
        const original = FacetFiltersForm.renderProductGridContainer.bind(FacetFiltersForm);
        FacetFiltersForm.renderProductGridContainer = (html) => {
          original(html);
          this.reset();
        };
        return true;
      };

      if (!tryBind()) {
        document.addEventListener('DOMContentLoaded', tryBind, { once: true });
        window.addEventListener('load', tryBind, { once: true });
      }
    }

    bindScrollFallback() {
      const onScroll = () => this.maybeLoad();
      window.addEventListener('scroll', onScroll, { passive: true });
      document.addEventListener('scroll', onScroll, { passive: true });
    }

    readNextUrl() {
      const container = document.getElementById('ProductGridContainer');
      const wrapper = document.querySelector('#ProductGridContainer .pagination-wrapper');
      if (wrapper && wrapper.dataset.nextUrl) return wrapper.dataset.nextUrl;
      if (container && container.dataset.nextUrl) return container.dataset.nextUrl;
      const nextLink = document.querySelector('#ProductGridContainer .pagination__item[aria-label*="Next"], #ProductGridContainer a[aria-label="Next page"]');
      return nextLink ? nextLink.getAttribute('href') : null;
    }

    reset() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      this.loading = false;
      this.grid = this.findGrid(document);
      if (!this.grid) return;

      const wrapper = document.querySelector('#ProductGridContainer .pagination-wrapper');
      this.nextUrl = this.readNextUrl();
      if (wrapper) wrapper.classList.add('rr-infinite-hide');

      if (!this.nextUrl) return;
      this.ensureSentinel();
      this.observe();
      this.maybeLoad();
    }

    findGrid(root) {
      const grid =
        root.querySelector('#ProductGridContainer ul.product-grid, #ProductGridContainer ul#product-grid') ||
        root.querySelector('#product-grid ul.product-grid') ||
        root.getElementById?.('product-grid') ||
        root.querySelector('#product-grid');
      if (!grid) return null;
      if (grid.tagName === 'UL') return grid;
      return grid.querySelector('ul.product-grid, ul');
    }

    ensureSentinel() {
      const container = document.getElementById('ProductGridContainer');
      if (!container) return;
      let sentinel = container.querySelector('.' + SENTINEL_CLASS);
      if (!sentinel) {
        sentinel = document.createElement('div');
        sentinel.className = SENTINEL_CLASS;
        sentinel.setAttribute('aria-hidden', 'true');
        sentinel.innerHTML = '<span class="rr-infinite-status" hidden>Loading more products…</span>';
        container.appendChild(sentinel);
      }
      this.sentinel = sentinel;
      this.status = sentinel.querySelector('.rr-infinite-status');
    }

    observe() {
      if (!('IntersectionObserver' in window) || !this.sentinel) return;
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.loadNext();
        },
        { rootMargin: ROOT_MARGIN_PX + 'px 0px' }
      );
      this.observer.observe(this.sentinel);
    }

    sentinelNear() {
      if (!this.sentinel) return false;
      const rect = this.sentinel.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
      return rect.top <= viewportHeight + ROOT_MARGIN_PX;
    }

    maybeLoad() {
      if (!this.loading && this.nextUrl && this.sentinelNear()) this.loadNext();
    }

    sectionFetchUrl(nextUrl) {
      const url = new URL(nextUrl, window.location.origin);
      const sectionId =
        (this.grid && this.grid.dataset.id) ||
        document.getElementById('product-grid')?.dataset.id;
      if (sectionId) url.searchParams.set('section_id', sectionId);
      return url.toString();
    }

    async loadNext() {
      if (this.loading || !this.nextUrl) return;
      this.loading = true;
      if (this.status) this.status.hidden = false;

      try {
        const response = await fetch(this.sectionFetchUrl(this.nextUrl), {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!response.ok) throw new Error('Failed to load more products');
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const nextGrid = this.findGrid(doc);
        if (nextGrid) {
          const fragment = document.createDocumentFragment();
          Array.from(nextGrid.children).forEach((item) => {
            item.querySelectorAll('.scroll-trigger').forEach((el) => {
              el.classList.add('scroll-trigger--cancel');
            });
            fragment.appendChild(item);
          });
          this.grid.appendChild(fragment);
        }

        const nextWrapper = doc.querySelector('#ProductGridContainer .pagination-wrapper');
        const nextContainer = doc.getElementById('ProductGridContainer');
        this.nextUrl =
          (nextWrapper && nextWrapper.dataset.nextUrl) ||
          (nextContainer && nextContainer.dataset.nextUrl) ||
          null;

        const liveWrapper = document.querySelector('#ProductGridContainer .pagination-wrapper');
        const liveContainer = document.getElementById('ProductGridContainer');
        if (this.nextUrl) {
          if (liveWrapper) liveWrapper.dataset.nextUrl = this.nextUrl;
          if (liveContainer) liveContainer.dataset.nextUrl = this.nextUrl;
        } else {
          if (liveWrapper) delete liveWrapper.dataset.nextUrl;
          if (liveContainer) delete liveContainer.dataset.nextUrl;
          if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
          }
        }
      } catch (error) {
        console.warn(error);
        const wrapper = document.querySelector('#ProductGridContainer .pagination-wrapper');
        if (wrapper) wrapper.classList.remove('rr-infinite-hide');
      } finally {
        this.loading = false;
        if (this.status) this.status.hidden = !this.loading;
        if (this.nextUrl && this.sentinelNear()) this.loadNext();
      }
    }
  }

  const start = () => {
    window.rrInfiniteScroll = new CollectionInfiniteScroll();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
